'use client';

import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export function FilterSidebarPanel({
  id,
  ariaLabel,
  isOpen,
  onClose,
  children,
  footer,
}: {
  id: string;
  ariaLabel: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!isOpen) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const previousOverflow = document.body.style.overflow;
    const update = () => {
      document.body.style.overflow = desktop.matches
        ? previousOverflow
        : 'hidden';
    };
    update();
    desktop.addEventListener('change', update);
    return () => {
      desktop.removeEventListener('change', update);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <aside
      id={id}
      aria-label={ariaLabel}
      tabIndex={-1}
      className={cn(
        'shrink-0 bg-background',
        isOpen
          ? 'fixed inset-x-0 bottom-0 top-[var(--mobile-header-height,140px)] z-40 flex flex-col pb-[env(safe-area-inset-bottom)]'
          : 'hidden',
        'lg:relative lg:inset-auto lg:z-auto lg:flex lg:overflow-hidden lg:overflow-y-visible lg:h-full lg:max-h-none lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] lg:border-b-0 lg:border-r lg:pb-0',
      )}
    >
      <div className="lg:hidden flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0 bg-background">
        <h2 className="text-sm font-semibold">{t('search.filters')}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.buttons.close')}
          className="inline-flex items-center justify-center w-11 h-11 rounded-full hover:bg-muted"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex flex-col w-full lg:w-[380px] lg:min-w-[380px] flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 flex flex-col">
          {children}
        </div>
        {footer && (
          <div className="lg:hidden p-4 border-t bg-background/95 supports-[backdrop-filter]:bg-background/90 backdrop-blur-xs flex flex-col gap-3 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </aside>
  );
}
